import React, { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { signInWithCustomToken, onIdTokenChanged, signOut } from 'firebase/auth';

import { store } from '../store';
import { setLoginInfo } from '../slices/statusSlice';
import API from '../lib/API';
import { auth } from '../lib/firebaseConfig';

/**
 * On cold load, reauthenticates using persistently stored tokens:
 * 1. Listens for Firebase ID token refreshes to keep local token synced.
 * 2. Attempts token exchange via api-auth/firebase/.
 * 3. Falls back to Django token verification via api-auth/verify/ if Firebase ID token is expired.
 */
const AuthBootstrap: React.FC = () => {
    const dispatch = useDispatch();
    const isReauthenticating = useRef<boolean>(false);

    useEffect(() => {
        // Keep stored Firebase token in sync when it rotates/refreshes
        const unsubscribe = onIdTokenChanged(auth, async (user) => {
            if (user) {
                try {
                    const freshIdToken = await user.getIdToken();
                    const currentStatus = store.getState().status;
                    if (currentStatus.token && currentStatus.firebaseToken !== freshIdToken) {
                        dispatch(
                            setLoginInfo({
                                token: currentStatus.token,
                                userID: currentStatus.userID ?? -1,
                                firebaseToken: freshIdToken,
                            })
                        );
                    }
                } catch (err) {
                    console.error('Error getting refreshed Firebase ID token:', err);
                }
            }
        });

        const performReauth = async () => {
            if (isReauthenticating.current) return;
            isReauthenticating.current = true;

            const { token, firebaseToken } = store.getState().status;
            if (!token && !firebaseToken) {
                return;
            }

            // Step 1: Attempt reauthentication via Firebase ID token exchange
            if (firebaseToken) {
                try {
                    const response = await API.exchangeFirebaseToken(firebaseToken);
                    const customToken = response.firebaseToken ?? '';
                    let firebaseTokenOut = '';
                    if (customToken) {
                        const credential = await signInWithCustomToken(auth, customToken);
                        firebaseTokenOut = await credential.user.getIdToken();
                    }
                    dispatch(
                        setLoginInfo({
                            token: response.token,
                            userID: response.userID,
                            firebaseToken: firebaseTokenOut,
                        })
                    );
                    return;
                } catch (err) {
                    console.warn('Firebase token exchange failed, falling back to Django token verification:', err);
                }
            }

            // Step 2: Fallback: Verify active Django token
            if (token) {
                try {
                    const response = await API.verifyAuth(token);
                    const customToken = response.firebaseToken ?? '';
                    let firebaseTokenOut = '';
                    if (customToken) {
                        try {
                            const credential = await signInWithCustomToken(auth, customToken);
                            firebaseTokenOut = await credential.user.getIdToken();
                        } catch (fbErr) {
                            console.warn('Firebase custom token signin error:', fbErr);
                        }
                    }
                    dispatch(
                        setLoginInfo({
                            token: response.token,
                            userID: response.userID,
                            firebaseToken: firebaseTokenOut,
                        })
                    );
                    return;
                } catch (err) {
                    console.warn('Django token verification failed:', err);
                }
            }

            // Step 3: If both methods failed, clear auth state
            try {
                await signOut(auth);
            } catch {
                /* ignore */
            }
            dispatch(setLoginInfo({ token: '', userID: -1, firebaseToken: '' }));
        };

        performReauth();

        return () => {
            unsubscribe();
        };
    }, [dispatch]);

    return null;
};

export default AuthBootstrap;
